import { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useScope, useScopeLabel } from '../hooks/useScope';
import { api } from '../api/client';
import { useQuery } from '../hooks/useApi';
import { Banner, EmptyState, ErrorState, Spinner } from '../components/States';
import { SkinImage } from '../components/SkinCard';
import { cleanName, count } from '../lib/format';
import { Discount } from '../components/Discount';
import { useCurrency } from '../hooks/useCurrency';
import { useI18n } from '../i18n';
import { itemDoTemplate, trackSelectItem, trackViewItemList } from '../lib/tracking';

export function VariantsPage() {
  const scope = useScope();
  const scopeLabel = useScopeLabel(scope);
  const { money } = useCurrency();
  const { t, loc } = useI18n();
  const { baseKey = '' } = useParams();
  const navigate = useNavigate();
  const key = decodeURIComponent(baseKey);

  const query = useQuery(
    () => api.variants(scope.apiScope, key),
    [scope.label, scope.isCategory, key],
    Boolean(scope.label && key),
  );

  // Os desgastes de uma skin são uma lista: a pessoa escolhe um deles para ver
  // os anúncios. O id da lista é a chave da skin, igual em qualquer idioma.
  const lista = { id: `desgastes:${key}`, nome: query.data ? cleanName(query.data.group.name) : key };
  useEffect(() => {
    if (!query.data) return;
    trackViewItemList(lista, query.data.group.variants.map((v, i) => itemDoTemplate(v, i)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.data]);

  return (
    <>
      <div className="crumbs">
        <Link to={scope.basePath}>{scopeLabel}</Link>
        <span className="sep">›</span>
        <b>{query.data ? cleanName(query.data.group.name) : t('common.loading')}</b>
        <span className="sep">›</span>
        <span>{t('level25.title')}</span>
      </div>

      {query.data?.source === 'demo' && <Banner kind="warn">{query.data.notice}</Banner>}
      {query.error && <ErrorState error={query.error} onRetry={query.refetch} />}
      {query.loading && <Spinner label={t('level25.loading')} />}

      {query.data && (
        <>
          <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
            <div className="skin-hero">
              <SkinImage url={query.data.group.iconUrl} alt={query.data.group.name} />
            </div>
            <div>
              <h2 style={{ margin: '0 0 4px', fontSize: 20 }}>
                {cleanName(query.data.group.name)}
                {query.data.group.isStatTrak && <span className="badge st" style={{ marginLeft: 8 }}>ST™</span>}
              </h2>
              <div style={{ color: 'var(--muted)', fontSize: 13 }}>
                {t('level25.summary', {
                  rarity: loc(query.data.group.rarity),
                  count: query.data.group.variantCount,
                  onSale: count(query.data.group.totalOnSale),
                })}
              </div>
            </div>
          </div>

          {query.data.group.variants.length === 0 ? (
            <EmptyState>{t('level25.empty')}</EmptyState>
          ) : (
            query.data.group.variants.map((variant, i) => (
              <div className="row" key={variant.id}>
                {variant.iconUrl ? (
                  <img className="thumb" src={variant.iconUrl} alt="" loading="lazy" />
                ) : (
                  <div className="thumb" />
                )}
                <div className="main">
                  <div className="title" style={{ color: variant.exterior?.color ?? 'var(--txt)' }}>
                    {variant.exterior ? loc(variant.exterior) : t('level25.unknownWear')}
                    {variant.exterior && (
                      <span style={{ color: 'var(--muted)', fontWeight: 400, fontSize: 12 }}>
                        {' '}
                        ({variant.exterior.code} · float {variant.exterior.floatRange[0]}–{variant.exterior.floatRange[1]})
                      </span>
                    )}
                  </div>
                  <div className="sub">
                    🛒 {t('level2.onSale', { n: count(variant.onSaleCount) })}
                  </div>
                </div>
                <div className="right">
                  <div className="price">{money(variant.price)}</div>
                  <div style={{ fontSize: 11, color: 'var(--muted)' }}>
                    {t('common.steam')} {money(variant.steamPrice)}{' '}
                    <Discount value={variant.discountPct} />
                  </div>
                </div>
                <button
                  className="btn"
                  onClick={() => {
                    trackSelectItem(lista, itemDoTemplate(variant, i));
                    navigate(`${scope.basePath}/t/${variant.id}`);
                  }}
                >
                  {t('level25.listings')} ›
                </button>
              </div>
            ))
          )}
        </>
      )}
    </>
  );
}
